import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import { Copy } from "lucide-react";
import * as React from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DrawerForm from "src/components/drawer-form/drawer-form";
import PaymentForm from "src/components/payment-add-form/payment-add-form";
import PaymentDescCard from "src/components/payment-desc-card/payment-desc-card";
import PaymentStatusLabel from "src/components/payment-status/payment-status";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import paymentsQueries from "src/queries/payments-queries";
import {
	CURRENCY_SYMBOL,
	type Payment,
	type PaymentParams,
	type PaymentStatus,
} from "src/types";
import { sleep } from "src/utils";
import copyToClipboard from "src/utils/copy-to-clipboard";
import { STATUS_CONFIG } from "src/utils/status-config";

const PAYMENT_STATUSES = [
	"pending",
	"locked",
	"inProgress",
	"success",
	"failed",
	"aml_rejected",
] as const;

const STATUS_OPTIONS = PAYMENT_STATUSES.map((status) => {
	const config = STATUS_CONFIG[status];
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: STATUS_OPTIONS,
	},
];

const displayPrice = (record: Payment) => {
	let ticker = `${record.currency} `;
	if (
		record.currency in CURRENCY_SYMBOL &&
		CURRENCY_SYMBOL[record.currency] !== ""
	) {
		ticker = CURRENCY_SYMBOL[record.currency];
	}

	return ticker + record.price;
};

const DEFAULT_PAGE_SIZE = 20;

export function PaymentsList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [isFormOpen, setFormOpen] = React.useState<boolean>(false);
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const [openedPayment, setOpenedPayment] = React.useState<Payment | null>(
		null,
	);
	const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = React.useState(false);

	const statusFilter = React.useMemo(() => {
		for (const filter of filters) {
			if (filter.field === "status" && filter.operator === "is") {
				return filter.value as PaymentStatus;
			}
		}
		return undefined;
	}, [filters]);

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: DEFAULT_PAGE_SIZE },
	});

	const page = pagination.page;
	const limit = pagination.limit;
	const setPage = (newPage: number) => setPagination({ page: newPage });
	const setLimit = (newLimit: number) =>
		setPagination({ limit: newLimit, page: 1 });

	const offset = (page - 1) * limit;

	const listPayments = paymentsQueries.listPaymentsPaginated({
		limit,
		offset,
		search: search || undefined,
		status: statusFilter,
	});

	const createPayment = paymentsQueries.createPayment();

	const isLoading =
		listPayments.isLoading ||
		createPayment.isPending ||
		listPayments.isFetching;

	const payments = listPayments.data?.results || [];
	const totalCount = listPayments.data?.totalCount || 0;

	const hasActiveFilters = search || filters.length > 0;

	const handleViewPayment = React.useCallback((payment: Payment) => {
		setOpenedPayment(payment);
		setIsPaymentDrawerOpen(true);
	}, []);

	const openNotification = React.useCallback(
		(title: string, description: string) => {
			toast.success(title, {
				description,
			});
		},
		[],
	);

	const columns: ColumnDef<Payment>[] = React.useMemo(
		() => [
			{
				id: "orderId",
				header: "Order ID",
				cell: (row) => (
					<button
						type="button"
						aria-label={`Copy order ID ${row.orderId || row.id}`}
						className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
						onClick={(e) => {
							e.stopPropagation();
							copyToClipboard(row.orderId || row.id, openNotification);
						}}
					>
						<span className="text-sm font-semibold">
							{row.orderId || row.id.slice(0, 8)}
						</span>
						<Copy
							aria-hidden="true"
							className="h-4 w-4 text-muted-foreground hover:text-primary"
						/>
					</button>
				),
			},
			{
				id: "price",
				header: "Price",
				cell: (row) => (
					<span className="whitespace-nowrap font-medium font-mono">
						{displayPrice(row)}
					</span>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<PaymentStatusLabel status={row.status} />
						{row.isTest && <StatusBadge type="warning">TEST</StatusBadge>}
					</div>
				),
			},
			{
				id: "customer",
				header: "Customer",
				cell: (row) => (
					<span className="text-sm text-muted-foreground">
						{row.additionalInfo?.payment?.customerEmail || "—"}
					</span>
				),
			},
			{
				id: "createdAt",
				header: "Created",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
		],
		[openNotification],
	);

	const uploadCreatedPayment = async (
		value: PaymentParams,
		reset: () => void,
	) => {
		try {
			setIsFormSubmitting(true);
			await createPayment.mutateAsync(value);
			setFormOpen(false);
			toast.success("Payment was created", {
				description: "You can now share the payment link with your customer.",
			});

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
			toast.error("Failed to create payment", {
				description:
					error instanceof Error ? error.message : "Please try again.",
			});
		} finally {
			setIsFormSubmitting(false);
		}
	};

	return (
		<>
			<div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex gap-4 flex-wrap items-center">
					<SearchInput
						placeholder="Search payments..."
						className="w-full sm:w-[300px]"
					/>
					<FilterBuilder
						fields={FILTER_FIELDS}
						filters={filters}
						onFiltersChange={setFilters}
					/>
				</div>
				<Button className="w-full sm:w-auto" onClick={() => setFormOpen(true)}>
					New Payment
				</Button>
			</div>

			<DataTable
				columns={columns}
				data={payments}
				isLoading={isLoading}
				onRowClick={handleViewPayment}
				getRowId={(row) => row.id}
				emptyMessage={
					hasActiveFilters ? "No payments found" : "No payments yet"
				}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your search criteria"
						: "Send a hosted checkout link to your customer to start collecting payments."
				}
				emptyAction={
					hasActiveFilters ? undefined : (
						<Button onClick={() => setFormOpen(true)}>
							Create your first payment
						</Button>
					)
				}
				pagination={
					totalCount > 0
						? {
								page,
								pageSize: limit,
								totalCount,
								onPageChange: setPage,
								onPageSizeChange: setLimit,
							}
						: undefined
				}
			/>

			<DrawerForm
				title="Payment details"
				isOpen={isPaymentDrawerOpen}
				onClose={() => setIsPaymentDrawerOpen(false)}
				width={512}
			>
				{openedPayment && (
					<PaymentDescCard
						data={openedPayment}
						openNotificationFunc={openNotification}
					/>
				)}
			</DrawerForm>

			<DrawerForm
				title="Create payment"
				description="Create a new payment request for your customer."
				isOpen={isFormOpen}
				onClose={() => setFormOpen(false)}
				onSubmit={() => {
					const form = document.querySelector("form");
					if (form) {
						form.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={isFormSubmitting}
				submitText="Create Payment"
				width={512}
			>
				<PaymentForm
					onFinish={uploadCreatedPayment}
					isFormSubmitting={isFormSubmitting}
				/>
			</DrawerForm>

			<Outlet />
		</>
	);
}
